import { useEffect, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { isMobile } from "react-device-detect";
import { useNavigate, useParams } from "react-router-dom";
import { useVoiceVisualizer } from "react-voice-visualizer";
import { transcribeSession as transcribeSessionService } from "services/Session.services";

import { ROUTES } from "utils/constants";
import { queryClient } from "utils/queryClient";

import { useWakeLockContext } from "context/WakeLock/useWakeLock";

export function useVoiceRecorder() {
  const { id } = useParams();

  const onTranscribeSuccess = () => {
    if (postActions === "stop") {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      queryClient.invalidateQueries({ queryKey: ["session", id] });
      navigate(ROUTES.HOME);
    } else if (postActions === "summary") {
      queryClient.invalidateQueries({ queryKey: ["session", id] });
    }
  };

  const transcribeSession = useMutation<any, Error, FormData>({
    mutationFn: (formData) => transcribeSessionService(id ?? "", formData),
    onSuccess: onTranscribeSuccess,
  });

  const transcribeFile = (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    transcribeSession.mutate(formData);
  };

  const [screenHeight, setScreenHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => setScreenHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getVisualizerHeight = () => {
    if (!isMobile) return 200;
    if (screenHeight < 700) return 110; // Mali mobilni ekrani
    if (screenHeight < 800) return 180; // Srednji mobilni ekrani
    return 200; // Veći mobilni ekrani
  };

  const navigate = useNavigate();

  const recorderControls = useVoiceVisualizer();
  const [recordingState, setRecordingState] = useState<"idle" | "recording" | "paused" | "stopped">("idle");
  const [postActions, setPostActions] = useState<"stop" | "summary">("stop");
  const [open, setOpen] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const { release } = useWakeLockContext();

  const handleButtonClick = () => {
    if (recordingState === "idle") {
      recorderControls.startRecording();
      setRecordingState("recording");
    } else if (recordingState === "recording") {
      recorderControls.togglePauseResume();
      setRecordingState("paused");
      release();
    } else if (recordingState === "paused") {
      recorderControls.togglePauseResume();
      setRecordingState("recording");
    }
  };

  const handleStopRecording = async () => {
    if (recordingState === "idle") {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      navigate(ROUTES.HOME);
    } else {
      recorderControls.stopRecording();
      setPostActions("stop");
      release();
    }
  };

  const handleTranscribeRecording = async () => {
    recorderControls.stopRecording();
    setPostActions("summary");
    setOpen(true);
  };

  useEffect(() => {
    if (postActions === "stop") {
      setOpen(true);
    }
    const blob = recorderControls.recordedBlob;
    if (blob) {
      const file = new File([blob], "recording.webm", {
        type: "audio/webm",
      });
      transcribeFile(file);
    }
  }, [recorderControls.recordedBlob]);

  return {
    transcribeSession,
    transcribeFile,
    isPending: transcribeSession.isPending,
    recordingState,
    handleButtonClick,
    handleStopRecording,
    handleTranscribeRecording,
    getVisualizerHeight,
    open,
    setOpen,
    pauseOpen,
    setPauseOpen,
    recorderControls,
  };
}
